// Founder-directed customer-facing billing-recovery visibility
// (2026-09-09) -- an app-wide banner, visible on every page to every
// authenticated user regardless of role, so a blocked/grace-period
// tenant is never a silent mystery to non-admin roles who can't reach
// /settings (TenantSettings.jsx restricts that whole page to
// admin/owner). Reads ONLY the canonical GET /billing/status
// `effective_state` + `grace_period_ends_at` -- the same fields
// BillingCard.jsx uses -- never a client-computed guess, and never
// exposes override metadata beyond what BillingCard already safely
// shows (in fact this banner shows none at all: `effective_state`
// already folds any active override into ACTIVE/COURTESY_ACCESS
// server-side, so GRACE_PERIOD/SUSPENDED here can never be a
// misfire against an overridden tenant).
//
// Role-appropriate action, matching the server's own
// `require_tenant_billing_admin` gate (owner/admin only):
//   - owner/admin: a real "Update payment" button that opens the
//     Stripe customer portal directly.
//   - every other role: plain text pointing them at their admin --
//     no billing controls rendered for a role that has no billing
//     privilege.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, XCircle, Loader2, CreditCard } from "lucide-react";

const BANNER_META = {
  GRACE_PERIOD: {
    tone: "warn",
    Icon: AlertTriangle,
    text: (dateStr) =>
      `Your last payment failed. Resolve billing by ${new Date(dateStr).toLocaleDateString()} to avoid losing access.`,
    fallbackText: "Your last payment failed. Resolve billing to avoid losing access.",
  },
  SUSPENDED: {
    tone: "bad",
    Icon: XCircle,
    text: () => "Access is suspended. Most actions are blocked until billing is resolved.",
    fallbackText: "Access is suspended. Most actions are blocked until billing is resolved.",
  },
};
const TONE_CLASS = {
  warn: "bg-[#3a2a0f] text-amber-100 border-b border-amber-800/60",
  bad: "bg-[#3a0f0f] text-red-100 border-b border-red-800/60",
};

export default function EntitlementStatusBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/billing/status")
      .then(({ data }) => { if (!cancelled) setStatus(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !status) return null;
  const meta = BANNER_META[status.effective_state];
  if (!meta) return null;

  const isBillingAdmin = ["admin", "owner"].includes(user.role);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.href,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  const message = status.grace_period_ends_at ? meta.text(status.grace_period_ends_at) : meta.fallbackText;

  return (
    <div
      className={`px-4 py-2 text-[12px] font-medium flex flex-wrap items-center justify-between gap-x-3 gap-y-1 min-w-0 ${TONE_CLASS[meta.tone]}`}
      data-testid="entitlement-status-banner"
    >
      <div className="inline-flex items-center gap-2 min-w-0">
        <meta.Icon size={14} className="shrink-0" />
        <span data-testid="entitlement-status-banner-message">{message}</span>
      </div>
      {isBillingAdmin ? (
        <button
          onClick={openPortal}
          disabled={portalBusy}
          className="inline-flex items-center gap-1.5 rounded-full border border-current/40 px-3 py-1 text-[11px] uppercase tracking-wide hover:bg-white/10 transition-colors shrink-0"
          data-testid="entitlement-status-banner-update-payment-btn"
        >
          {portalBusy ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
          Update payment
        </button>
      ) : (
        <span className="text-[11px] opacity-85 shrink-0" data-testid="entitlement-status-banner-contact-admin">
          Contact your account owner or admin to resolve this.
        </span>
      )}
    </div>
  );
}
